import { useDynamicsRecordLink } from '../../hooks/useDynamicsRecordLink';
import { useDynamicsPage } from '../../hooks/useDynamicsPage';
/**
 * Composant HelpdeskPage
 *
 * Page dédiée à l'affichage détaillé des tickets d'assistance.
 * Fonctionnalités :
 * - Fil d'ariane pour navigation
 * - Filtres par statut et priorité
 * - Liste détaillée des tickets avec toutes les informations
 * - Badges visuels pour statut, priorité, SLA
 *
 * @param {number} projectId - ID du projet (optionnel)
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, Clock, User, X, MessageSquare, Plus, Paperclip, Trash2, Send, ChevronLeft, ChevronRight, Check, Search, SlidersHorizontal } from 'lucide-react';
import { useProjectData } from "../../context/ProjectDataContext";
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { stripHtml } from '../../utils/htmlUtils';
import backendApi from '../../services/backendApi';
import ThemeToggle from '../common/ThemeToggle';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';
import SupportTriageModal from './SupportTriageModal';
import DynamicsIcon from '../common/DynamicsIcon';
import TicketAttachments from './TicketAttachments';
import TicketHistoryPanel from './TicketHistoryPanel';
import {durationHours, durationLabel, groupTicketsByPeriod, periodLabel} from '../../utils/ticketHistory';
import { readTicketFile, selectTicketFiles } from '../../utils/ticketAttachments';
import '../../styles/horizonHome.css';
import '../../styles/horizonAssistance.css';
import '../../styles/storyDetail.css';

const HelpdeskPage = () => {
  const { isInternalUser } = useAuth();

  // Récupérer le projectId depuis localStorage
  const projectId = typeof localStorage !== 'undefined'
    ? parseInt(localStorage.getItem('selected_project_id'))
    : null;

  // Lire les données depuis le contexte partagé (pas de nouvel appel API)
  const { tickets, helpdeskStages, loading, error, refresh, upsertTicket, updateTicketStage } = useProjectData();

  // État pour la recherche (avec debounce 300ms)
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // État pour les filtres de date
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [activeTicketView, setActiveTicketView] = useState('open');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [listPage, setListPage] = useState(1);

  // État pour la modal de détails
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [messagePage, setMessagePage] = useState(1);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const messageRequest = useRef(0);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState(null);
  const [closingTicketId, setClosingTicketId] = useState(null);
  const [closeNotice, setCloseNotice] = useState('');
  const [classifications, setClassifications] = useState({});
  const [classificationError, setClassificationError] = useState('');
  const [classificationProgress, setClassificationProgress] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState('week');
  const [aiSearch, setAiSearch] = useState(null);
  const historyRef = useRef(null);

  // État pour la modal de création
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTriageModal, setShowTriageModal] = useState(false);
  const [triageAnchor, setTriageAnchor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: '1'
  });
  const [attachments, setAttachments] = useState([]);
  const [readingAttachments, setReadingAttachments] = useState(false);
  const ticketModalExit = useAnimatedModalClose(() => setSelectedTicket(null));
  const createModalExit = useAnimatedModalClose(() => setShowCreateModal(false), { disabled: creating || readingAttachments });
  const closeTicketAnimated = ticketModalExit.close;
  const closeCreateAnimated = createModalExit.close;

  const handleTriageComplete = useCallback((ticket) => {
    if (ticket) {
      upsertTicket(ticket);
      setActiveTicketView(ticket.stageId === 4 ? 'closed' : 'open');
      setListPage(1);
      setSearchQuery('');
      setDebouncedSearch('');
      setDateFrom('');
      setDateTo('');
    }
  }, [upsertTicket]);

  const handleAiSearch = useCallback((query, results) => {
    setAiSearch({query,results});
    setSearchQuery('');
    setDebouncedSearch('');
    setDateFrom('');
    setDateTo('');
    setActiveTicketView('all');
    setListPage(1);
    setShowHistory(true);
  }, []);

  useEffect(() => {
    if (!aiSearch || !showHistory) return undefined;
    const timer = setTimeout(() => historyRef.current?.scrollIntoView?.({behavior:'smooth',block:'start'}), 0);
    return () => clearTimeout(timer);
  }, [aiSearch, showHistory]);

  // Charger les messages quand un ticket est sélectionné
  const fetchMessages = useCallback(async (page = 1) => {
    const ticketId = selectedTicket?.id;
    const requestId = ++messageRequest.current;
    if (!ticketId) {
      setTicketMessages([]);
      setHasOlderMessages(false);
      setMessagePage(1);
      setLoadingMessages(false);
      setLoadingOlderMessages(false);
      return;
    }

    if (page === 1) {
      setTicketMessages([]);
      setLoadingMessages(true);
      setLoadingOlderMessages(false);
      setHasOlderMessages(false);
    } else setLoadingOlderMessages(true);
    try {
      const data = await backendApi.getTicketMessages(ticketId, page);
      if (requestId !== messageRequest.current) return;
      setTicketMessages(previous => page === 1 ? data.messages || [] : [...previous, ...(data.messages || [])]);
      setHasOlderMessages(Boolean(data.hasMore));
      setMessagePage(page);
      setMessageError(null);
    } catch (error) {
      if (requestId !== messageRequest.current) return;
      console.error('Erreur chargement messages:', error);
      if (page === 1) setTicketMessages([]);
      setMessageError({ ticketId, text: 'Impossible de charger les échanges. Réessayez.' });
    } finally {
      if (requestId === messageRequest.current) {
        setLoadingMessages(false);
        setLoadingOlderMessages(false);
      }
    }
  }, [selectedTicket?.id]);

  useEffect(() => {
    const tracker = messageRequest;
    fetchMessages(1);
    return () => { tracker.current++; };
  }, [fetchMessages]);

  // Envoyer un message
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!selectedTicket || !newMessage.trim() || sendingMessage) {
      return;
    }

    const ticketId = selectedTicket.id;
    setMessageError(null);
    setSendingMessage(true);
    try {
      await backendApi.createTicketMessage(ticketId, newMessage);

      // Réinitialiser le champ
      setNewMessage('');

      // Rafraîchir les messages
      await fetchMessages();
    } catch (error) {
      console.error('Erreur envoi message:', error);
      setMessageError({ ticketId, text: error.message || 'Erreur lors de l\'envoi du message' });
    } finally {
      setSendingMessage(false);
    }
  };

  // Clôturer un ticket
  const handleCloseTicket = async (ticketId, e) => {
    e.stopPropagation(); // Empêcher l'ouverture de la modal

    if (closingTicketId !== null) return;

    if (!window.confirm('Voulez-vous vraiment clôturer ce ticket ?')) {
      return;
    }

    try {
      setClosingTicketId(ticketId);
      await backendApi.closeTicket(ticketId);
      const stageName = helpdeskStages.find(stage => stage.id === 4)?.name || 'Clôturé';
      updateTicketStage(ticketId, 4, stageName);
      setSelectedTicket(current => current?.id === ticketId ? { ...current, stageId: 4, stageName } : current);
      setCloseNotice(`Ticket #${ticketId} clôturé avec succès.`);
    } catch (error) {
      console.error('Erreur clôture ticket:', error);
      alert(error.message || 'Erreur lors de la clôture du ticket');
    } finally {
      setClosingTicketId(null);
    }
  };

  const handleReportBotAnswer = async (ticketId) => {
    const feedback = window.prompt('Pourquoi la réponse Dynamix est-elle incorrecte ?');
    if (!feedback?.trim()) return;
    try {
      await backendApi.reportSupportAnswer(ticketId, feedback.trim());
      alert('Le signalement a été ajouté au ticket.');
      await fetchMessages();
    } catch (reportError) {
      alert(reportError.response?.data?.error || reportError.message || 'Signalement impossible');
    }
  };

  // Fermer la modal avec la touche Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (selectedTicket) {
          closeTicketAnimated();
        }
        if (showCreateModal) {
          closeCreateAnimated();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeCreateAnimated, closeTicketAnimated, selectedTicket, showCreateModal]);

  // Gérer l'ajout de fichiers
  const handleFileChange = async (e) => {
    const { accepted, error } = selectTicketFiles(Array.from(e.target.files), attachments);
    e.target.value = '';
    if (error) alert(error);
    if (!accepted.length) return;
    setReadingAttachments(true);
    try {
      const files = await Promise.all(accepted.map(readTicketFile));
      setAttachments(previous => [...previous, ...files]);
    } catch {
      alert('Impossible de lire une pièce jointe.');
    } finally {
      setReadingAttachments(false);
    }
  };

  // Supprimer un fichier
  const handleRemoveFile = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Gérer la création d'un ticket
  const handleCreateTicket = async (e) => {
    e.preventDefault();

    if (readingAttachments) return;
    if (!formData.name.trim()) {
      alert('Le titre du ticket est requis');
      return;
    }

    setCreating(true);
    try {
      await backendApi.createTicket({
        name: formData.name,
        description: formData.description,
        priority: formData.priority,
        project_id: projectId,
        attachments: attachments
      });

      // Réinitialiser le formulaire
      setFormData({ name: '', description: '', priority: '1' });
      setAttachments([]);
      createModalExit.close();

      // Rafraîchir la liste des tickets
      refresh();

      alert('Votre ticket a été créé et transmis à l’équipe d’assistance.');
    } catch (error) {
      console.error('Erreur création ticket:', error);
      if (!error.authSessionExpired) {
        alert(error.message || 'Erreur lors de la création du ticket');
      }
    } finally {
      setCreating(false);
    }
  };

  // Fonction pour obtenir le label de priorité
  const getPriorityLabel = (priority) => {
    const priorityMap = {
      '0': 'Basse',
      '1': 'Normale',
      '2': 'Haute',
      '3': 'Urgente'
    };
    return priorityMap[priority] || 'Non définie';
  };

  // Fonction pour vérifier si proche du SLA
  const isNearSLA = (slaDeadline) => {
    if (!slaDeadline) return false;
    const now = new Date();
    const deadline = new Date(slaDeadline);
    const diffHours = (deadline - now) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= 24;
  };

  // Filtrer les tickets par recherche (debounced) et dates
  const filteredTickets = useMemo(() => {
    let filtered = tickets;

    if (aiSearch) {
      const ids = new Set(aiSearch.results.map(result => result.id));
      filtered = filtered.filter(ticket => ids.has(ticket.id));
      const position = new Map(aiSearch.results.map((result,index) => [result.id,index]));
      filtered = [...filtered].sort((a,b) => position.get(a.id) - position.get(b.id));
    }

    // Filtre par recherche (utilise debouncedSearch pour éviter le re-render à chaque frappe)
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(ticket => {
        // Recherche par numéro
        if (ticket.id.toString().includes(query)) return true;

        // Recherche par nom/désignation
        if (ticket.name?.toLowerCase().includes(query)) return true;

        // Recherche par description
        if (ticket.description) {
          const cleanDescription = stripHtml(ticket.description).toLowerCase();
          if (cleanDescription.includes(query)) return true;
        }
        if (ticket.actionsTaken?.toLowerCase().includes(query)) return true;
        if (ticket.actualResult?.toLowerCase().includes(query)) return true;

        return false;
      });
    }

    // Filtre par date de début
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(ticket => {
        if (!ticket.createDate) return false;
        const ticketDate = new Date(ticket.createDate);
        ticketDate.setHours(0, 0, 0, 0);
        return ticketDate >= fromDate;
      });
    }

    // Filtre par date de fin
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(ticket => {
        if (!ticket.createDate) return false;
        const ticketDate = new Date(ticket.createDate);
        return ticketDate <= toDate;
      });
    }

    return filtered;
  }, [tickets, debouncedSearch, dateFrom, dateTo, aiSearch]);

  const ticketGroups = useMemo(() => {
    const stageById = new Map(helpdeskStages.map(stage => [stage.id, stage]));
    const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const bucketFor = ticket => {
      const stage = stageById.get(ticket.stageId);
      const name = normalize(ticket.stageName || stage?.name);
      if (name.includes('annul') || name.includes('cancel')) return 'cancelled';
      if (ticket.stageId === 4 || stage?.fold || name.includes('clotur') || name.includes('ferme')) return 'closed';
      return 'open';
    };
    const statusFor = ticket => {
      const stage = stageById.get(ticket.stageId);
      const name = normalize(ticket.stageName || stage?.name);
      if (ticket.stageId === 6 || name.includes('besoin')) return 'need';
      if (ticket.stageId === 1 || name === 'nouveau') return 'new';
      if (ticket.stageId === 2 || name.includes('cours')) return 'progress';
      if (ticket.stageId === 3 || name.includes('attente')) return 'waiting';
      if (ticket.stageId === 7 || name.includes('resolu')) return 'resolved';
      return 'other';
    };
    const groups = { all: [], open: [], needs: [], closed: [], cancelled: [] };
    filteredTickets.forEach(ticket => {
      const assistanceStatus = statusFor(ticket);
      const enrichedTicket = { ...ticket, assistanceStatus };
      groups.all.push(enrichedTicket);
      groups[bucketFor(ticket)].push(enrichedTicket);
      if (assistanceStatus === 'need') groups.needs.push(enrichedTicket);
    });
    return groups;
  }, [filteredTickets, helpdeskStages]);

  const allTicketGroups = useMemo(() => {
    const stageById = new Map(helpdeskStages.map(stage => [stage.id, stage]));
    const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const counts = { need: 0, new: 0, progress: 0, waiting: 0, resolved: 0 };
    tickets.forEach(ticket => {
      const name = normalize(ticket.stageName || stageById.get(ticket.stageId)?.name);
      if (ticket.stageId === 6 || name.includes('besoin')) counts.need += 1;
      else if (ticket.stageId === 1 || name === 'nouveau') counts.new += 1;
      else if (ticket.stageId === 2 || name.includes('cours')) counts.progress += 1;
      else if (ticket.stageId === 3 || name.includes('attente')) counts.waiting += 1;
      else if (ticket.stageId === 7 || name.includes('resolu')) counts.resolved += 1;
    });
    return counts;
  }, [tickets, helpdeskStages]);

  const displayedTickets = ticketGroups[activeTicketView];
  const confirmationTickets = activeTicketView === 'open' ? displayedTickets.filter(ticket => ticket.assistanceStatus === 'resolved') : [];
  const regularTickets = activeTicketView === 'open' ? displayedTickets.filter(ticket => ticket.assistanceStatus !== 'resolved') : displayedTickets;
  const listPageCount = Math.max(1, Math.ceil(regularTickets.length / 20));
  const visibleRegularTickets = regularTickets.slice((listPage - 1) * 20, listPage * 20);
  const visibleTicketGroups = showHistory ? groupTicketsByPeriod(visibleRegularTickets,historyPeriod) : [{period:null,tickets:visibleRegularTickets}];
  const confirmationGroups = showHistory ? groupTicketsByPeriod(confirmationTickets,historyPeriod) : [{period:null,tickets:confirmationTickets}];

  const classificationIds = [...new Set([
    selectedTicket?.id,
    ...confirmationTickets.slice(0, 5).map(ticket => ticket.id),
    ...visibleRegularTickets.map(ticket => ticket.id),
  ].filter(Boolean))].slice(0, 20);
  const classificationKey = classificationIds.join(',');
  const classifyAll = async () => {
    if (!projectId || classificationProgress !== null) return;
    setClassificationError('');
    setClassificationProgress(0);
    try {
      const remaining = filteredTickets.filter(ticket => !classifications[ticket.id]).map(ticket => ticket.id);
      const alreadyClassified = filteredTickets.length - remaining.length;
      if (!remaining.length) return;
      const {jobId} = await backendApi.startTicketClassification(projectId,remaining);
      let status;
      do {
        status = await backendApi.getTicketClassification(jobId);
        const latestClassifications = status.classifications;
        setClassifications(previous => ({...previous,...latestClassifications}));
        setClassificationProgress(alreadyClassified + status.completed);
        if (status.status !== 'complete') await new Promise(resolve => setTimeout(resolve,2000));
      } while (status.status !== 'complete');
      if (status.failures.length) setClassificationError(`${status.failures.reduce((sum,item) => sum + item.count,0)} ticket(s) restent à analyser. ${status.failures[0].reason || 'Réessayez dans un instant.'}`);
    } catch (error) { setClassificationError(error.response?.data?.error || error.message || 'Analyse indisponible. Réessayez.'); }
    finally { setClassificationProgress(null); }
  };
  useEffect(() => {
    if (!projectId || !classificationIds.length) return undefined;
    let active = true;
    setClassificationError('');
    backendApi.classifyTickets(projectId, classificationIds).then(result => {
      if (active) setClassifications(previous => ({...previous, ...result.classifications}));
    }).catch(() => { if (active) setClassificationError('Analyse des types indisponible pour le moment.'); });
    return () => { active = false; };
  // Reclassify only when the displayed page of tickets changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, classificationKey]);

  useEffect(() => { setListPage(1); }, [activeTicketView, debouncedSearch, dateFrom, dateTo, projectId]);

  useDynamicsRecordLink(tickets, setSelectedTicket);
  useDynamicsPage({path:'/dashboard/tickets',title:selectedTicket ? selectedTicket.name : 'Assistance',kind:'tickets',recordIds:(selectedTicket ? [selectedTicket] : filteredTickets).map(t => t.id),filters:{search: searchQuery, dateFrom, dateTo},ready:!loading});

  if (loading && !showCreateModal && !showTriageModal) {
    return (
      <div className="min-h-full bg-gray-50">
        <LoadingSpinner message="Chargement des tickets..." />
      </div>
    );
  }

  if (error && !showCreateModal && !showTriageModal) {
    return (
      <div className="min-h-full bg-gray-50">
        <ErrorMessage message={error} onRetry={refresh} />
      </div>
    );
  }

  return (
    <div className="horizon-home horizon-assistance">
      <div className="horizon-home-inner">
        <header className="horizon-header">
          <div className="horizon-header-row">
            <div>
              <p className="horizon-breadcrumb">Votre assistance</p>
              <h1>Assistance</h1>
              <p className="horizon-subtitle">Retrouvez vos demandes et échangez avec notre équipe.</p>
            </div>
            <div className="horizon-header-actions">
              <ThemeToggle />
              {isInternalUser && <button type="button" className="assistance-primary" onClick={() => setShowCreateModal(true)}><Plus size={18} />Nouveau ticket</button>}
              <div className="assistance-bot-launcher"><span className="assistance-bot-tooltip" role="tooltip">Signaler un problème, faire une demande ou chercher un ticket.</span><button type="button" className="assistance-bot-button" aria-label="Demander de l’aide à Dynamix" onClick={event => { const rect = event.currentTarget.getBoundingClientRect(); setTriageAnchor({ top: rect.bottom + 14, right: window.innerWidth - rect.right }); setShowTriageModal(true); }}><DynamicsIcon size={60} /></button></div>
            </div>
          </div>
        </header>
        <div className="assistance-content">
          {closeNotice && <p role="status" className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{closeNotice}</p>}
          <section className="assistance-status-overview" aria-label="Résumé des tickets par statut">
            {[
              ['need', 'Nouveau besoin'],
              ['new', 'Nouveau'],
              ['progress', 'En cours'],
              ['waiting', 'En attente'],
              ['resolved', 'Résolu']
            ].map(([status, label]) => (
              <div className={`assistance-status assistance-status--${status}`} key={status}>
                <span><i aria-hidden="true" />{label}</span>
                <strong>{allTicketGroups[status]}</strong>
              </div>
            ))}
          </section>

          <div className="assistance-history-toggle"><button type="button" className="assistance-history__analyze" aria-expanded={showHistory} onClick={() => setShowHistory(value => !value)}>{showHistory ? 'Masquer l’historique et les graphiques' : 'Afficher l’historique et les graphiques'}</button></div>
          {showHistory && <div ref={historyRef}><TicketHistoryPanel tickets={filteredTickets} classifications={classifications} onClassifyAll={classifyAll} classificationProgress={classificationProgress} period={historyPeriod} onPeriodChange={setHistoryPeriod} />
            {classificationError && <p role="status" className="assistance-classification-error">{classificationError}</p>}</div>}
          {aiSearch && <section className="assistance-ai-results" aria-label="Résultats de la recherche Dynamix"><div><strong>Recherche IA : {aiSearch.query}</strong><p>{aiSearch.results.length ? `${aiSearch.results.length} ticket${aiSearch.results.length > 1 ? 's' : ''} proposé${aiSearch.results.length > 1 ? 's' : ''} par Dynamix.` : 'Aucun ticket pertinent trouvé.'}</p></div><button type="button" onClick={() => {setAiSearch(null);setActiveTicketView('open');}}>Effacer la recherche IA</button></section>}

          <section className="assistance-toolbar" aria-label="Recherche et affichage des tickets">
            <label className="assistance-search">
              <Search size={18} aria-hidden="true" />
              <span className="sr-only">Filtrer la liste des tickets</span>
              <input type="search" placeholder="Filtrer la liste affichée…" value={searchQuery} onChange={event => {setAiSearch(null);setActiveTicketView('open');setSearchQuery(event.target.value);}} />
            </label>
            <div className="assistance-view-tabs" role="tablist" aria-label="État des tickets">
              {[
                ...(aiSearch ? [['all', 'Résultats IA']] : []),
                ['open', 'Ouverts'],
                ['needs', 'Nouveaux besoins'],
                ['closed', 'Clôturés'],
                ['cancelled', 'Annulés']
              ].map(([view, label]) => (
                <button key={view} type="button" role="tab" aria-selected={activeTicketView === view} onClick={() => setActiveTicketView(view)}>
                  {label} <span>· {ticketGroups[view].length}</span>
                </button>
              ))}
            </div>
            <button type="button" className="assistance-more-filters" aria-expanded={showAdvancedFilters} onClick={() => setShowAdvancedFilters(value => !value)}>
              <SlidersHorizontal size={17} />{showAdvancedFilters ? 'Masquer les filtres' : 'Plus de filtres'}
            </button>
          </section>

          <section aria-hidden={!showAdvancedFilters} inert={showAdvancedFilters ? undefined : ''} className={`assistance-advanced-filters${showAdvancedFilters ? ' is-expanded' : ' is-collapsed'}`} aria-label="Filtres avancés des tickets">
            <label>Créé à partir du<input type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></label>
            <label>Jusqu’au<input type="date" value={dateTo} onChange={event => setDateTo(event.target.value)} /></label>
            {(searchQuery || dateFrom || dateTo) && <button type="button" onClick={() => { setSearchQuery(''); setDateFrom(''); setDateTo(''); }}>Réinitialiser</button>}
          </section>

          <div className="sr-only" aria-live="polite">{displayedTickets.length} ticket{displayedTickets.length > 1 ? 's' : ''} affiché{displayedTickets.length > 1 ? 's' : ''}</div>

          {confirmationTickets.length > 0 && <section key={`confirm-${searchQuery}-${dateFrom}-${dateTo}`} className="assistance-section filter-results-motion" aria-labelledby="tickets-to-confirm">
            <div className="assistance-section-title">
              <h2 id="tickets-to-confirm">À confirmer par vous</h2>
              <p>Notre équipe a résolu ces demandes. Vérifiez puis clôturez.</p>
            </div>
            <div className="assistance-confirmation-list">
              {confirmationGroups.map(group => <div key={group.period || 'flat'} className="assistance-ticket-period-group">
                {showHistory && <div className="assistance-ticket-period-heading"><strong>{periodLabel(group.period,historyPeriod)}</strong><span>{group.tickets.length} ticket{group.tickets.length > 1 ? 's' : ''}</span></div>}
                {group.tickets.map(ticket => <article className="assistance-confirmation" key={ticket.id}>
                <div className="assistance-confirmation-icon"><Check size={22} /></div>
                <div className="assistance-confirmation-copy">
                  <div className="assistance-ticket-meta">
                    <span>#{ticket.id}</span>
                    {ticket.resolvedDate && <><i>•</i><span>Résolu le {new Date(ticket.resolvedDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span></>}
                    {(ticket.priority === '2' || ticket.priority === '3') && <span className="assistance-priority">Priorité {ticket.priority === '3' ? 'urgente' : 'haute'}</span>}
                  </div>
                  <h3>{ticket.name}</h3>
                  <p className="assistance-ticket-resolution">Délai de résolution : {durationLabel(durationHours(ticket.createDate, ticket.resolvedDate))}</p>
                </div>
                <div className="assistance-confirmation-actions">
                  <button type="button" className="assistance-secondary" onClick={() => setSelectedTicket(ticket)}>Voir l’échange</button>
                  <button type="button" className="assistance-primary" disabled={closingTicketId !== null} onClick={event => handleCloseTicket(ticket.id, event)}>{closingTicketId === ticket.id ? 'Clôture…' : 'Clôturer'}</button>
                </div>
                </article>)}
              </div>)}
            </div>
          </section>}

          <section key={`${activeTicketView}-${searchQuery}-${dateFrom}-${dateTo}-${listPage}`} className="assistance-section filter-results-motion" aria-labelledby="ticket-list-title">
            <div className="assistance-section-title">
              <h2 id="ticket-list-title">{activeTicketView === 'all' ? 'Tickets trouvés par Dynamix' : activeTicketView === 'open' ? 'Nouveaux tickets' : activeTicketView === 'needs' ? 'Nouveaux besoins' : activeTicketView === 'closed' ? 'Tickets clôturés' : 'Tickets annulés'}</h2>
              <p>{regularTickets.length} demande{regularTickets.length > 1 ? 's' : ''}{['open', 'needs'].includes(activeTicketView) ? ' suivie' + (regularTickets.length > 1 ? 's' : '') + ' par notre équipe' : ''}</p>
            </div>
            {regularTickets.length > 0 ? <div className="assistance-ticket-table">
              {visibleTicketGroups.map(group => <div key={group.period || 'flat'} className="assistance-ticket-period-group">
                {showHistory && <div className="assistance-ticket-period-heading"><strong>{periodLabel(group.period,historyPeriod)}</strong><span>{group.tickets.length} ticket{group.tickets.length > 1 ? 's' : ''}</span></div>}
                {group.tickets.map(ticket => <button type="button" className="assistance-ticket-row" key={ticket.id} onClick={() => setSelectedTicket(ticket)}>
                <span className="assistance-ticket-number">#{ticket.id}</span>
                <span className="assistance-ticket-summary"><strong>{ticket.name}</strong><small className="assistance-ticket-resolution">Délai de résolution : {durationLabel(durationHours(ticket.createDate, ticket.resolvedDate))}</small>{aiSearch && <small className="assistance-ticket-reason">{aiSearch.results.find(result => result.id === ticket.id)?.reason}</small>}</span>
                <span className="assistance-ticket-type">{classifications[ticket.id] || 'Analyse IA en attente'}</span>
                {ticket.createDate && <time dateTime={ticket.createDate}>{new Date(ticket.createDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</time>}
                <ChevronRight size={18} aria-hidden="true" />
                </button>)}
              </div>)}
              {listPageCount > 1 && <nav className="assistance-pagination" aria-label="Pagination des tickets">
                <span>{(listPage - 1) * 20 + 1}–{Math.min(listPage * 20, regularTickets.length)} sur {regularTickets.length}</span>
                <div>
                  <button type="button" aria-label="Page précédente" disabled={listPage === 1} onClick={() => setListPage(page => Math.max(1, page - 1))}><ChevronLeft size={17} /></button>
                  <strong>{listPage} / {listPageCount}</strong>
                  <button type="button" aria-label="Page suivante" disabled={listPage === listPageCount} onClick={() => setListPage(page => Math.min(listPageCount, page + 1))}><ChevronRight size={17} /></button>
                </div>
              </nav>}
            </div> : <div className="assistance-empty">
              <AlertCircle size={22} aria-hidden="true" />
              <p>{filteredTickets.length === 0 && tickets.length > 0 ? 'Aucun ticket ne correspond à vos filtres.' : `Aucun ${activeTicketView === 'needs' ? 'nouveau besoin' : activeTicketView === 'open' ? 'ticket ouvert' : activeTicketView === 'closed' ? 'ticket clôturé' : 'ticket annulé'} pour le moment.`}</p>
            </div>}
          </section>

        </div>
      </div>
      {/* Modal de détails du ticket — même structure que la User Story */}
      {selectedTicket && (
        <div className={`us-detail-overlay assistance-ticket-detail ${ticketModalExit.modalClassName} fixed inset-0 z-[60] flex items-center justify-center p-6`}
          onClick={ticketModalExit.close}>
          <div className="us-detail-window bg-white overflow-hidden flex flex-col shadow-2xl" onClick={event => event.stopPropagation()}
            role="dialog" aria-modal="true" aria-labelledby="ticket-detail-title">
            <header className="us-detail-header">
              <div className="us-detail-header__meta">
                <strong>Ticket #{selectedTicket.id}</strong>
                {selectedTicket.createDate && <><span aria-hidden="true">•</span><span>Créé le {new Date(selectedTicket.createDate).toLocaleDateString('fr-FR')}</span></>}
                <span aria-hidden="true">•</span><span>Priorité {getPriorityLabel(selectedTicket.priority).toLowerCase()}</span>
                <em>{selectedTicket.stageName || 'Nouveau'}</em>
                {selectedTicket.botOutcome && <span>Dynamix · {selectedTicket.botOutcome === 'resolved' ? 'résolu par le bot' : 'qualifié'}</span>}
                <span>Type IA : {classifications[selectedTicket.id] || 'Analyse en attente'}</span>
              </div>
              <h2 id="ticket-detail-title">{selectedTicket.name}</h2>
              <button onClick={ticketModalExit.close} aria-label="Fermer le ticket" className="us-detail-header__close">
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="testing-detail-body">
              <div className="px-6 py-6">
                <div className="assistance-history__metrics assistance-history__metrics--detail">
                  <div><span>Résolution</span><strong>{durationLabel(durationHours(selectedTicket.createDate, selectedTicket.resolvedDate))}</strong><small>{selectedTicket.resolvedDate ? `Le ${new Date(selectedTicket.resolvedDate).toLocaleDateString('fr-FR')}` : 'Date non renseignée'}</small></div>
                  <div><span>Clôture</span><strong>{durationLabel(durationHours(selectedTicket.createDate, selectedTicket.closedDate))}</strong><small>{selectedTicket.closedDate ? `Le ${new Date(selectedTicket.closedDate).toLocaleDateString('fr-FR')}` : 'Date non renseignée'}</small></div>
                </div>
                {isNearSLA(selectedTicket.slaDeadline) && <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-800">
                  <Clock className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-semibold">Échéance SLA proche : {new Date(selectedTicket.slaDeadline).toLocaleString('fr-FR')}</span>
                </div>}
                {(selectedTicket.actionsTaken || selectedTicket.actualResult) && <div className="mb-6 grid gap-4 sm:grid-cols-2">
                  <section className="rounded-lg border border-gray-200 bg-gray-50 p-5">
                    <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-gray-500">Actions effectuées</h3>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{selectedTicket.actionsTaken || 'Non renseignées'}</p>
                  </section>
                  <section className="rounded-lg border border-gray-200 bg-gray-50 p-5">
                    <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-gray-500">Résultat obtenu</h3>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{selectedTicket.actualResult || 'Non renseigné'}</p>
                  </section>
                </div>}
                {selectedTicket.description ? <div className="mb-6">
                  <h3 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">Description</h3>
                  <div className="prose prose-base max-w-none rounded-lg border border-gray-200 bg-gray-50 p-6 text-base text-gray-700"
                    dangerouslySetInnerHTML={{ __html: selectedTicket.description }} />
                </div> : <p className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">Aucune description renseignée.</p>}
                {selectedTicket.slaDeadline && !isNearSLA(selectedTicket.slaDeadline) && <div className="mt-6">
                  <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-gray-500">Échéance SLA</h3>
                  <p className="text-sm font-semibold text-gray-800">{new Date(selectedTicket.slaDeadline).toLocaleString('fr-FR')}</p>
                </div>}
                {isInternalUser && selectedTicket.botOutcome && <button type="button" className="mt-6 text-sm font-semibold text-red-700 underline"
                  onClick={() => handleReportBotAnswer(selectedTicket.id)}>Signaler une mauvaise réponse Dynamix</button>}
              </div>

              <div className="us-chatter-pane testing-discussion bg-gray-50 flex flex-col border-l border-gray-200">
                <div className="us-detail-tabs">
                  <h3 className="sr-only">Échanges du ticket</h3>
                  <p className="sr-only">Historique des échanges externes</p>
                  <div className="us-detail-tabs__row">
                    <button type="button" className="is-active"><MessageSquare className="h-4 w-4" /> Échanges</button>
                    <TicketAttachments ticketId={selectedTicket.id} compact />
                    <span>{ticketMessages.length} message{ticketMessages.length > 1 ? 's' : ''}{hasOlderMessages ? ' affichés' : ''}</span>
                  </div>
                </div>

                <div className="us-detail-composer p-4 bg-white border-t border-gray-200 flex-shrink-0">
                  {messageError?.ticketId === selectedTicket.id && <p role="alert" className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{messageError.text}</p>}
                  <form onSubmit={handleSendMessage} className="flex flex-col gap-2">
                    <textarea value={newMessage} onChange={event => setNewMessage(event.target.value)} placeholder="Écrire un commentaire..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B4C9A] focus:border-[#6B4C9A] resize-none text-sm"
                      rows="3" disabled={sendingMessage} />
                    <div className="flex justify-end"><button type="submit" disabled={sendingMessage || !newMessage.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#6B4C9A] text-white rounded-lg hover:bg-[#59407F] transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                      {sendingMessage ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Envoi...</> : <><Send className="w-4 h-4" />Envoyer</>}
                    </button></div>
                  </form>
                </div>

                <div className="chatter-message-list p-6 pb-0">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#6B4C9A]"></div>
                    </div>
                  ) : ticketMessages.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded border border-gray-200">
                      <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-base text-gray-500 mb-2">Aucune correspondance</p>
                      <p className="text-sm text-gray-400">Les notes internes sont masquées</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {ticketMessages.map((message) => {
                        const isEmail = message.messageType === 'email';
                        return (
                          <div
                            key={message.id}
                            className={`rounded-lg p-4 border shadow-sm ${
                              isEmail
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            {/* En-tête du message */}
                            <div className="flex items-start gap-3 mb-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isEmail ? 'bg-blue-200' : 'bg-[#E9E0F5]'
                              }`}>
                                {isEmail ? (
                                  <span className="text-base">📧</span>
                                ) : (
                                  <User className="w-4 h-4 text-[#6B4C9A]" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">
                                  {message.authorName}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {message.date && new Date(message.date).toLocaleString('fr-FR', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>

                            {/* Sujet (si présent) */}
                            {message.subject && (
                              <p className="text-sm font-bold text-gray-800 mb-3">
                                {message.subject}
                              </p>
                            )}

                            {/* Corps du message */}
                            {message.body && (
                              <div
                                className="text-sm text-gray-700 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: message.body }}
                              />
                            )}
                          </div>
                        );
                      })}
                      {hasOlderMessages && <button type="button" disabled={loadingOlderMessages} onClick={() => fetchMessages(messagePage + 1)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#6B4C9A] hover:bg-gray-50 disabled:opacity-50">
                        {loadingOlderMessages ? 'Chargement…' : 'Afficher les messages plus anciens'}
                      </button>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="us-detail-footer">
              <button onClick={ticketModalExit.close} className="us-detail-footer__close">Fermer</button>
              {selectedTicket.stageId === 7 && <div className="us-detail-footer__actions">
                <button type="button" disabled={closingTicketId !== null} onClick={event => handleCloseTicket(selectedTicket.id, event)}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-green-700">
                  <Check className="h-4 w-4" />Clôturer
                </button>
              </div>}
            </footer>
          </div>
        </div>
      )}

      {showTriageModal && <SupportTriageModal projectId={projectId} anchor={triageAnchor}
        onClose={() => setShowTriageModal(false)} onComplete={handleTriageComplete} onSearchComplete={handleAiSearch} />}

      {/* Modal de création directe réservée aux utilisateurs internes */}
      {showCreateModal && (
        <div className={`assistance-create-overlay ${createModalExit.modalClassName} fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4`}>
          <div className="dashboard-modal__surface bg-white rounded-lg max-w-2xl w-full overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-[#E9E0F5]">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="w-6 h-6 text-[#6B4C9A]" />
                  Nouveau ticket d'assistance
                </h2>
                <button
                  onClick={createModalExit.close}
                  aria-label="Fermer la création"
                  className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-200 rounded"
                  disabled={creating || readingAttachments}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Message d'instruction */}
            <div className="px-6 pt-4 pb-2">
              <p className="text-sm text-gray-600">
                Remplissez les informations ci-dessous pour créer votre ticket d'assistance. Un membre de notre équipe vous répondra dans les plus brefs délais.
              </p>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleCreateTicket} className="p-6 pt-2">
              <div className="space-y-4">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Titre du ticket — résumé du problème <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B4C9A] focus:border-[#6B4C9A]"
                    placeholder="Ex. : La connexion échoue avec une erreur d’accès"
                    maxLength={100}
                    required
                    disabled={creating}
                  />
                </div>

                {/* Priorité */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Priorité
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B4C9A] focus:border-[#6B4C9A]"
                    disabled={creating}
                  >
                    <option value="0">Basse</option>
                    <option value="1">Normale</option>
                    <option value="2">Haute</option>
                    <option value="3">Urgente</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B4C9A] focus:border-[#6B4C9A] min-h-[120px]"
                    placeholder="Décrivez le problème en détail..."
                    disabled={creating}
                  />
                </div>

                {/* Pièces jointes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Pièces jointes
                  </label>

                  {/* Bouton d'ajout */}
                  <label className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#8768B5] hover:bg-[#E9E0F5] transition-colors cursor-pointer">
                    <Plus className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      Ajouter des fichiers (Word, Excel, vidéos…)
                    </span>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={creating || readingAttachments}
                    />
                  </label>
                  <p className="mt-2 text-xs text-gray-500">10 Mo par document, 30 Mo par vidéo, 40 Mo au total (10 fichiers maximum).</p>

                  {/* Liste des fichiers */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                            disabled={creating}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={createModalExit.close}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  disabled={creating || readingAttachments}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#6B4C9A] text-white rounded-lg hover:bg-[#59407F] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={creating || readingAttachments}
                >
                  {creating || readingAttachments ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {readingAttachments ? 'Préparation des fichiers…' : 'Création...'}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Créer le ticket
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpdeskPage;
